import Badge from '@material-ui/core/Badge'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import { makeStyles } from '@material-ui/styles'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import Button from 'core/elements/button'
import IconButton from 'core/elements/button/IconButton'
import Dropdown from 'core/elements/dropdown'
import { useGridFilterRowContainer } from 'core/elements/grid/GridFilterRowContext'
import Input from 'core/elements/input'
import ServerSideFilters, { type FilterConfig } from './ServerSideFilters'
import ServerSideSort, { type SortFieldOption } from './ServerSideSort'

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },
  firstCol: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  secondCol: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  searchSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  searchInputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  searchDropdown: {
    minWidth: 150,
    '& ul': {
      left: '-1px',
    },
    '& .inputFrame': {
      borderWidth: 0,
      borderRadius: 0,
      borderLeft: `1px solid ${theme.palette.divider}`,
      marginTop: 0,
      minHeight: 34,
    },
  },
  searchButton: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    '& button': {
      borderWidth: 0,
      borderRadius: 0,
      height: 34,
      padding: '0 12px',
    },
  },
  searchInput: {
    minWidth: 200,
    flex: 1,
    gap: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    '& .inputFrame': {
      borderWidth: 0,
      borderRadius: 0,
      minHeight: 34,
    },
    '& input': {
      minHeight: 34,
    },
  },
  iconButton: {
    width: 36,
    height: 36,
  },
  secondRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
    padding: theme.spacing(2),
    flexWrap: 'wrap',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    '& button': {
      marginBottom: theme.spacing(1),
    },
  },
}))

export interface SearchFieldOption {
  value: string
  label: string
}

export interface ServerSideSearchParams {
  searchField: string
  searchValue: string
  filters: Record<string, string>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

export interface ServerSideSearchProps {
  /** Available search field options */
  searchFields: SearchFieldOption[]
  /** Filter configurations (optional) */
  filterConfigs?: FilterConfig[]
  /** Sort field options (optional) */
  sortOptions?: SortFieldOption[]
  /** Callback when search/filter/sort is triggered */
  onSearch: (params: ServerSideSearchParams) => void
  /** Callback when clear all is triggered */
  onClear: () => void
  /** Loading state */
  isLoading?: boolean
  /** Initial search params from session */
  initialParams?: Partial<ServerSideSearchParams>
  /** Default sort key */
  defaultSortKey?: string
  /** Default sort direction */
  defaultSortDir?: 'asc' | 'desc'
  /** Additional content to render (e.g., toggles) */
  extraContent?: React.ReactNode
  /** Format search value for display (e.g., convert = to :) */
  formatSearchValue?: (searchField: string, searchValue: string) => string
}

/**
 * Generic server-side search component.
 *
 * Combines search input, filters, and sort into a unified toolbar.
 * All parameters are passed to the parent via callbacks for API calls.
 */
const ServerSideSearch: React.FC<ServerSideSearchProps> = ({
  searchFields,
  filterConfigs = [],
  sortOptions = [],
  onSearch,
  onClear,
  isLoading = false,
  initialParams,
  defaultSortKey,
  defaultSortDir = 'desc',
  extraContent,
  formatSearchValue,
}) => {
  const classes = useStyles()
  const filterRowContainer = useGridFilterRowContainer()

  // Initialize state from initial params or defaults
  const [searchField, setSearchField] = useState<string>(
    initialParams?.searchField || searchFields[0]?.value || 'name',
  )
  const [searchValue, setSearchValue] = useState<string>(initialParams?.searchValue || '')
  const [sortKey, setSortKey] = useState<string>(
    initialParams?.sortKey || defaultSortKey || sortOptions[0]?.value || 'created_at',
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialParams?.sortDir || defaultSortDir)

  // Initialize filter values with defaults
  const getDefaultFilterValues = useCallback(() => {
    const defaults: Record<string, string> = {}
    filterConfigs.forEach((filter) => {
      defaults[filter.key] = filter.defaultValue
    })
    return defaults
  }, [filterConfigs])

  const [filterValues, setFilterValues] = useState<Record<string, string>>(
    initialParams?.filters || getDefaultFilterValues(),
  )

  // Initialize active filter keys from initial params (filters with non-default values)
  const [activeFilterKeys, setActiveFilterKeys] = useState<string[]>(() => {
    if (!initialParams?.filters) return []
    return filterConfigs
      .filter(
        (f) =>
          initialParams.filters![f.key] !== undefined &&
          initialParams.filters![f.key] !== f.defaultValue,
      )
      .map((f) => f.key)
  })

  // Sync sort state when changed externally (e.g., column header sort)
  useEffect(() => {
    if (initialParams?.sortKey !== undefined) {
      setSortKey(initialParams.sortKey)
    }

    if (initialParams?.sortDir !== undefined) {
      setSortDir(initialParams.sortDir)
    }
  }, [initialParams?.sortKey, initialParams?.sortDir])

  const [showFilters, setShowFilters] = useState<boolean>(activeFilterKeys.length > 0)

  // Add filter menu state
  const [addFilterAnchor, setAddFilterAnchor] = useState<null | HTMLElement>(null)

  // Format search value if formatSearchValue is provided or provide the trimmed value
  const getFormattedSearchValue = useCallback(() => {
    return formatSearchValue
      ? formatSearchValue(searchField, searchValue.trim())
      : searchValue.trim()
  }, [searchField, searchValue, formatSearchValue])

  // Build current params object
  const buildParams = useCallback((): ServerSideSearchParams => {
    return {
      searchField,
      searchValue: getFormattedSearchValue(),
      filters: filterValues,
      sortKey,
      sortDir,
    }
  }, [searchField, getFormattedSearchValue, filterValues, sortKey, sortDir])

  // Handle search button click
  const handleSearch = useCallback(() => {
    onSearch(buildParams())
  }, [buildParams, onSearch])

  // Handle filter value change
  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  // Handle sort change
  const handleSort = useCallback(
    (newSortKey: string, newSortDir: 'asc' | 'desc') => {
      setSortKey(newSortKey)
      setSortDir(newSortDir)
      // Trigger search immediately with new sort
      onSearch({
        searchField,
        searchValue: getFormattedSearchValue(),
        filters: filterValues,
        sortKey: newSortKey,
        sortDir: newSortDir,
      })
    },
    [searchField, searchValue, filterValues, onSearch, getFormattedSearchValue],
  )

  // Handle remove filter
  const handleRemoveFilter = useCallback(
    (key: string) => {
      const defaultVal = filterConfigs.find((f) => f.key === key)?.defaultValue ?? ''
      const newFilterValues = { ...filterValues, [key]: defaultVal }
      setActiveFilterKeys((prev) => {
        const next = prev.filter((k) => k !== key)
        if (next.length === 0) setShowFilters(false)
        return next
      })
      setFilterValues(newFilterValues)
      onSearch({
        searchField,
        searchValue: getFormattedSearchValue(),
        filters: newFilterValues,
        sortKey,
        sortDir,
      })
    },
    [filterConfigs, filterValues, onSearch, searchField, getFormattedSearchValue, sortKey, sortDir],
  )

  // Handle add filter menu open
  const handleAddFilterClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAddFilterAnchor(event.currentTarget)
  }, [])

  // Handle add filter menu close
  const handleAddFilterClose = useCallback(() => {
    setAddFilterAnchor(null)
  }, [])

  // Handle add filter selection
  const handleAddFilterSelect = useCallback(
    (filterKey: string) => {
      setActiveFilterKeys((prev) => [...prev, filterKey])
      handleAddFilterClose()
    },
    [handleAddFilterClose],
  )

  // Handle clear all
  const handleClearAll = useCallback(() => {
    setSearchField(searchFields[0]?.value || 'name')
    setSearchValue('')
    setFilterValues(getDefaultFilterValues())
    setActiveFilterKeys([])
    setShowFilters(false)
    setSortKey(defaultSortKey || sortOptions[0]?.value || 'created_at')
    setSortDir(defaultSortDir)
    onClear()
  }, [searchFields, getDefaultFilterValues, defaultSortKey, sortOptions, defaultSortDir, onClear])

  // Handle input change with auto-clear detection
  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      const prevValue = searchValue

      // If clearing the input, trigger search with empty value
      if (prevValue.trim() !== '' && newValue.trim() === '') {
        onSearch({
          searchField,
          searchValue: '',
          filters: filterValues,
          sortKey,
          sortDir,
        })
      }

      setSearchValue(newValue)
    },
    [searchValue, searchField, filterValues, sortKey, sortDir, onSearch],
  )

  // Handle enter key
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && searchValue?.trim() !== '') {
        handleSearch()
      }
    },
    [handleSearch, searchValue],
  )

  // Check if any filter or search is active
  const hasActiveFilters = useMemo(() => {
    const hasActiveFilter = Object.entries(filterValues).some(([key, value]) => {
      const config = filterConfigs.find((f) => f.key === key)
      return value !== config?.defaultValue
    })
    return hasActiveFilter || searchValue.trim() !== ''
  }, [filterValues, filterConfigs, searchValue])

  const hasFilters = filterConfigs.length > 0
  const hasSort = sortOptions.length > 0

  // Only show active filters in the row
  const activeFilterConfigs = useMemo(
    () => filterConfigs.filter((f) => activeFilterKeys.includes(f.key)),
    [filterConfigs, activeFilterKeys],
  )

  // Available filters not yet added
  const availableFilterItems = filterConfigs
    .filter((f) => f.visible !== false && !activeFilterKeys.includes(f.key))
    .map((f) => ({ value: f.key, label: f.label }))

  const filterRow =
    hasFilters && showFilters ? (
      <div className={classes.secondRow}>
        <ServerSideFilters
          filters={activeFilterConfigs}
          values={filterValues}
          onChange={handleFilterChange}
          disabled={isLoading}
          onRemove={handleRemoveFilter}
        />
        <div className={classes.actionButtons}>
          {availableFilterItems.length > 0 && (
            <>
              <Button variant="secondary" onClick={handleAddFilterClick} disabled={isLoading}>
                + Add Filter
              </Button>
              <Menu
                anchorEl={addFilterAnchor}
                open={Boolean(addFilterAnchor)}
                onClose={handleAddFilterClose}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {availableFilterItems.map((item) => (
                  <MenuItem
                    key={item.value}
                    onClick={() => handleAddFilterSelect(item.value as string)}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {activeFilterKeys.length > 0 && (
            <Button onClick={handleSearch} disabled={isLoading}>
              Apply
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={handleClearAll}
            disabled={isLoading}
            rightIcon="times"
          >
            Clear All
          </Button>
        </div>
      </div>
    ) : null

  return (
    <div className={classes.root}>
      <div className={classes.firstCol}>
        {/* Search Section */}
        <div className={classes.searchSection}>
          <div className={classes.searchInputGroup}>
            <Input
              className={classes.searchInput}
              placeholder={`Search by ${searchFields
                .find((opt) => opt.value === searchField)
                ?.label.toLowerCase()}...`}
              value={searchValue}
              onChange={handleSearchInputChange}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
              clearable
            />
            <Dropdown
              className={classes.searchDropdown}
              items={searchFields}
              value={searchField}
              onChange={(value) => setSearchField(value as string)}
              disabled={isLoading}
            />
            <div className={classes.searchButton}>
              <IconButton
                onClick={handleSearch}
                disabled={isLoading || !searchValue.trim()}
                size="md"
                info="Search"
                icon="search"
              />
            </div>
          </div>
          {hasFilters && (
            <Badge badgeContent={!showFilters ? activeFilterKeys.length : 0} color="primary">
              <IconButton
                className={classes.iconButton}
                onClick={() => setShowFilters(!showFilters)}
                disabled={isLoading}
                info={showFilters ? 'Hide Filters' : 'Show Filters'}
                icon="filter"
              />
            </Badge>
          )}
        </div>

        <div className={classes.secondCol}>
          {extraContent}
          {hasSort && (
            <ServerSideSort
              sortOptions={sortOptions}
              onSort={handleSort}
              isLoading={isLoading}
              sortKey={sortKey}
              sortDir={sortDir}
            />
          )}
        </div>
      </div>

      {filterRow && filterRowContainer ? createPortal(filterRow, filterRowContainer) : filterRow}
    </div>
  )
}

export default ServerSideSearch
